import { SizeType } from 'antd/es/config-provider/SizeContext';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { TimePickerProps } from 'antd';
import { Dayjs } from 'dayjs';

type FieldDefaultProps = {
  maxLength: number;
  size: SizeType;
};

export const fieldDefaultProps: FieldDefaultProps = {
  maxLength: 100,
  size: 'large',
};

type TimePickerConfig = {
  size: SizeType;
  showSecond: boolean;
  inputReadOnly: boolean;
  allowClear: boolean;
  format: string | ((time: Dayjs) => string) | (string | ((time: Dayjs) => string))[];
  cellRender?: (current: string | number | Dayjs, info?: any) => ReactNode;
  placeholder?: string;
  disabled?: boolean;
  onSelect?: (time: Dayjs | null) => void;
  onChange?: (date: Dayjs | null, dateString: string | string[]) => void;
  use12Hours?: boolean;
  style?: React.CSSProperties;
  'data-testid'?: string;
};

export const timePickerDefaultProps: TimePickerConfig = {
  size: 'large',
  showSecond: false,
  inputReadOnly: false,
  allowClear: true,
  format: 'HH:mm', // Default HH:MM format
  placeholder: 'Select time', // Default placeholder
};

// Validation rule for HH:MM format
export const validateTimeFormat = (t: (key: string) => string) => ({
  validator: (_: any, value: Dayjs | null) => {
    if (!value) return Promise.resolve();

    const timeString = value.format('HH:mm');
    const isValidFormat = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(timeString);

    if (!isValidFormat) {
      return Promise.reject(new Error(t('common.validation.invalidTimeFormat')));
    }

    return Promise.resolve();
  },
});

// Reusable function to create TimePicker props with locale support and HH:MM validation
export const createTimePickerProps = (
  timeFormat: string,
  formatTimeWithLocale: (time: Dayjs, format: string) => string,
  renderTimeWithLocale: (current: string | number | Dayjs, info?: any) => ReactNode,
  additionalProps: Partial<TimePickerConfig> = {},
): TimePickerProps => ({
  ...timePickerDefaultProps,
  format: [timeFormat, (time: Dayjs) => formatTimeWithLocale(time, timeFormat)],
  cellRender: renderTimeWithLocale,
  // User-defined placeholder overrides default
  placeholder: additionalProps.placeholder || timePickerDefaultProps.placeholder,
  ...additionalProps,
});

export const customizeRequiredMark = (label: ReactNode, { required }: { required: boolean }): ReactNode => {
  const { t } = useTranslation();
  const isRTL = document.dir === 'rtl';
  const optionalText = `(${t('common.optional')})`;
  const spacing = isRTL ? ' ' : ' ';

  return required ? label : `${label}${spacing}${optionalText}`;
};
